import React, { useEffect, useState, useMemo } from "react";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import KLButton from "../../components/KLButton";
import { cancelSlot } from "../../store/duckers/slots/thunk";
import {
  ButtonWrapper,
  Container,
  Title,
  SlotButtons,
} from "../styles";
import {
  ServiceDescription,
  ServicePriceDuration,
  SlotContentWrapper,
  SlotInfo,
  SlotItem,
  SlotWhen,
  CancellationExplanation,
} from "./styles";
import {
  checkCustomerSlots,
  fetchBusiness,
} from "../../store/duckers/customers/thunk";
import KLPleaseWait from "../../components/KLPleaseWait";
import {
  BusinessCategory,
  BusinessCategoryGender,
  getCategoryConfigTerms,
} from "../../constants/customers/categoryConfigMap";
import { durationOptions, formatWhen } from "../../utils";
import { useStep } from "../../context/StepContext";
import { CustomerRouteStep, FlowType } from "../../constants/routes";
import KLChangeUpcomingSlotModal from "../../components/KLChangeUpcomingSlotModal";
import { KLSlot, UserType } from "../../constants/types";
import useLocalStorage from "../../hooks/useLocalStorage";
import { StorageKey, StorageKeys } from "../../constants/localStorage";
import KLEmptyState from "../../components/KLEmptyState";
import { EmptyCalendar } from "../../components/KLEmptyState/illustrations";

const CustomerExistingSlotsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { markStepAsCompleted } = useStep();
  const dispatch = useDispatch<AppDispatch>();
  const { businessId } = useParams<{ businessId: string }>();

  const {
    userData: tokenPayload,
    verifyingToken,
    isTokenVerified,
    redirectToken,
  } = useSelector((state: RootState) => state.tokens);

  const { setValue: setTokenValue } = useLocalStorage<StorageKey | null>(
    StorageKeys.VerifiedCustomerOtpToken,
    null,
  );

  const {
    fetchingCustomerSlots,
    customerPhone,
    customerSlots,
    business,
    customerName,
    fetchingBusiness,
  } = useSelector((state: RootState) => state.customers);
  const {
    cancellingSlot,
    cancelSlotSucceeded,
    changingSlot,
    changeSlotSucceeded,
  } = useSelector((state: RootState) => state.slots);
  const [cancellingSlotId, setCancellingSlotId] = useState<string | null>(null);
  const [changingSlotId, setChangingSlotId] = useState<string | null>(null);

  const customerSlotToKLSlot = useMemo(() => {
    if (!changingSlotId || !customerPhone) return null;
    const slot = customerSlots.find((slot) => slot.id === changingSlotId);
    if (!slot) return null;

    return {
      ...slot,
      name: customerName || "",
      phone: customerPhone,
    } as KLSlot;
  }, [changingSlotId, customerSlots, customerPhone, customerName]);

  useEffect(() => {
    if (businessId && !business && !fetchingBusiness) {
      dispatch(fetchBusiness(businessId));
    }
  }, [businessId, business, fetchingBusiness, dispatch]);

  useEffect(() => {
    if (redirectToken && isTokenVerified) {
      setTokenValue(redirectToken as StorageKey);
    }
  }, [redirectToken, isTokenVerified, setTokenValue]);

  useEffect(() => {
    if (!businessId) {
      navigate("/not-authorized");
      return;
    }

    if (verifyingToken) return;

    // Prefer the phone we already have in Redux (set during the booking flow
    // that just completed). Fall back to the phone inside a verified token
    // for direct visits to /existing-slots (e.g. tapping the "manage
    // appointment" banner on CustomerMainScreen after a page reload).
    // The JWT payload field is `phoneNumber` — see backend routes/auth.ts
    // where verify-otp calls generateJwtToken({ phoneNumber, userType }).
    const phoneForLookup =
      customerPhone ||
      (isTokenVerified ? tokenPayload?.phoneNumber : undefined);

    if (phoneForLookup) {
      dispatch(checkCustomerSlots({ businessId, phone: phoneForLookup }));
    } else {
      navigate("/not-authorized");
    }
  }, [
    businessId,
    customerPhone,
    tokenPayload,
    verifyingToken,
    isTokenVerified,
    dispatch,
    navigate,
  ]);

  useEffect(() => {
    if (cancelSlotSucceeded) {
      setCancellingSlotId(null);
    }
  }, [cancelSlotSucceeded]);

  useEffect(() => {
    if (changeSlotSucceeded) {
      setChangingSlotId(null);
    }
  }, [changeSlotSucceeded]);

  const handleCancelRequest = (slotId: string) => {
    if (!businessId) {
      return;
    }
    setCancellingSlotId(slotId);
    dispatch(cancelSlot({ businessId, slotId }));
  };

  const handleChangeRequest = (slotId: string) => {
    setChangingSlotId(slotId);
  };

  const handleCloseChangeModal = () => {
    setChangingSlotId(null);
  };

  const handleNewAppointment = () => {
    markStepAsCompleted(FlowType.Customer, CustomerRouteStep.ExistingSlots);
    navigate(`/${businessId}/services`);
  };

  const calculateDuration = (start: string, end: string) => {
    const startTime = DateTime.fromISO(start);
    const endTime = DateTime.fromISO(end);
    const durationInMinutes = endTime.diff(startTime, "minutes").minutes;
    return (
      durationOptions.find((option) => option.value === durationInMinutes)
        ?.label || `${durationInMinutes} דקות`
    );
  };

  const { term, pluralTerm, gender } = getCategoryConfigTerms(
    business?.category as BusinessCategory,
  );

  const generateCancellationExplanation = (cancellationPolicy: number) => {
    let explanation = "";
    const starts = gender === BusinessCategoryGender.Male ? "מתחיל" : "מתחילה";
    if (cancellationPolicy === 1) {
      explanation = `לא ניתן לבטל, ${term} ${starts} עוד פחות משעה`;
    } else if (cancellationPolicy === 2) {
      explanation = `לא ניתן לבטל, ${term} ${starts} עוד פחות משעתיים`;
    } else if (cancellationPolicy > 2) {
      explanation = `לא ניתן לבטל, ${term} ${starts} עוד פחות מ-${cancellationPolicy} שעות`;
    }

    return explanation && business ? (
      <>
        {explanation}
        <br />
        לבירורים אפשר ליצור קשר עם {business.businessName}
        <br />
        בטלפון {business.phone}
      </>
    ) : (
      ""
    );
  };

  const canCancel = (start: string, cancellationPolicy: number) => {
    const startTime = DateTime.fromISO(start).setZone("Asia/Jerusalem");
    const now = DateTime.now().setZone("Asia/Jerusalem");

    const hoursUntilStart = startTime.diff(now, "hours").hours;
    return hoursUntilStart > cancellationPolicy;
  };

  const canChange = (start: string) => {
    const startTime = DateTime.fromISO(start).setZone("Asia/Jerusalem");
    const now = DateTime.now().setZone("Asia/Jerusalem");
    return startTime > now;
  };

  if (fetchingCustomerSlots || fetchingBusiness) {
    return <KLPleaseWait text="עוד רגע זה קורה" />;
  }

  return (
    <Container>
      {customerSlots.length === 0 ? (
        <KLEmptyState
          illustration={<EmptyCalendar />}
          title={`אין לך ${pluralTerm} ${gender === BusinessCategoryGender.Female ? "עתידיות" : "עתידיים"}`}
          description={`נראה שהיומן שלך ריק כרגע. אפשר לקבוע ${pluralTerm} ${gender === BusinessCategoryGender.Female ? "חדשות" : "חדשים"}!`}
          size="medium"
        />
      ) : (
        <>
          <Title>
            הנה ה{pluralTerm} ה
            {gender === BusinessCategoryGender.Female ? "עתידיות" : "עתידיים"} שלך:
          </Title>
          {
        customerSlots.map((customerSlot, index) => {
          const when = formatWhen(customerSlot.start, customerSlot.end);
          const cancellationDisabled = !canCancel(
            customerSlot.start,
            customerSlot.cancellationPolicy,
          );
          const changeDisabled = !canChange(customerSlot.start);
          const explanation = cancellationDisabled
            ? generateCancellationExplanation(customerSlot.cancellationPolicy)
            : "";

          return (
            <SlotItem key={`${customerSlot.id}-${index}`}>
              <SlotContentWrapper>
                <SlotInfo>
                  <ServiceDescription>
                    {customerSlot.service}
                  </ServiceDescription>
                  <ServicePriceDuration>
                    {customerSlot.price && `${customerSlot.price} ש״ח , `}
                    {calculateDuration(customerSlot.start, customerSlot.end)}
                  </ServicePriceDuration>
                  <SlotWhen>
                    <span>{when.date}</span>, <span>{when.time}</span>
                  </SlotWhen>
                </SlotInfo>
                <SlotButtons>
                  <KLButton
                    title="שינוי"
                    onClick={() => handleChangeRequest(customerSlot.id)}
                    noWidth
                    noMarginTop
                    disabled={
                      changeDisabled || cancellingSlot || changingSlot
                    }
                  />
                  <KLButton
                    title="ביטול"
                    onClick={() => handleCancelRequest(customerSlot.id)}
                    warning
                    noWidth
                    noMarginTop
                    disabled={
                      cancellationDisabled || cancellingSlot || changingSlot
                    }
                    loading={
                      cancellingSlot && cancellingSlotId === customerSlot.id
                    }
                  />
                </SlotButtons>
              </SlotContentWrapper>
              {cancellationDisabled && (
                <CancellationExplanation>{explanation}</CancellationExplanation>
              )}
              {changingSlotId === customerSlot.id &&
                businessId &&
                customerSlotToKLSlot && (
                  <KLChangeUpcomingSlotModal
                    open={true}
                    handleClose={handleCloseChangeModal}
                    slot={customerSlotToKLSlot}
                    term={term}
                    businessId={businessId}
                    userType={UserType.Customer}
                  />
                )}
            </SlotItem>
          );
        })}
        </>
      )}
      <ButtonWrapper>
        <KLButton
          title={`קביעת ${term} ${gender === BusinessCategoryGender.Male ? "חדש" : "חדשה"}`}
          onClick={handleNewAppointment}
        />
      </ButtonWrapper>
    </Container>
  );
};

export default CustomerExistingSlotsScreen;
